import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FiArrowRight,
  FiCheck,
  FiChevronLeft,
  FiChevronRight,
  FiHeadphones,
  FiLock,
  FiMail,
  FiPlus,
  FiSearch,
  FiTruck,
} from "react-icons/fi";
import toast from "react-hot-toast";
import { formatPrice } from "../../utils/format";
import { sanitizeImageUrl } from "../../utils/imageUtils";
import { useCart } from "../../context/CartContext";
import StorefrontContentBlock from "./StorefrontContentBlock";
import "../../styles/storefront-homepage-templates.css";

const TEMPLATE_COPY = {
  "modern-minimal": {
    eyebrow: "THOUGHTFULLY CHOSEN",
    title: "A little better, every day.",
    description: "Discover considered essentials for the way you live.",
  },
  "luxury-boutique": {
    eyebrow: "THE CONSIDERED EDIT",
    title: "Make room for the exceptional.",
    description: "Explore timeless pieces, selected with care and made to last.",
  },
  "bold-contemporary": {
    eyebrow: "NEW SEASON. NEW ENERGY.",
    title: "Make your everyday a statement.",
    description: "Fresh finds, standout quality, and offers worth a second look.",
  },
  "fashion-lifestyle": {
    eyebrow: "A STUDY IN EVERYDAY STYLE",
    title: "Wear what moves you.",
    description: "Build your own point of view with this season's considered edit.",
  },
  "everyday-essentials": {
    eyebrow: "GOOD VALUE. GOOD EVERY DAY.",
    title: "Everything you need, made easy.",
    description: "Find the things you use, love, and reach for every day.",
  },
};

const TEMPLATE_HERO_IMAGES = {
  "modern-minimal":
    "https://images.unsplash.com/photo-1490312278390-ab64016e0aa9?auto=format&fit=crop&w=1800&q=85",
  "luxury-boutique":
    "https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&w=1800&q=85",
  "bold-contemporary":
    "https://images.unsplash.com/photo-1490481651871-ab68de25d43d?auto=format&fit=crop&w=1800&q=85",
  "fashion-lifestyle":
    "https://images.unsplash.com/photo-1483985988355-763728e1935b?auto=format&fit=crop&w=1800&q=85",
  "everyday-essentials":
    "https://images.unsplash.com/photo-1490312278390-ab64016e0aa9?auto=format&fit=crop&w=1800&q=85",
};

const TEMPLATE_LAYOUTS = {
  "modern-minimal": ["categories", "featured", "bestsellers", "promotion", "benefits", "newsletter"],
  "luxury-boutique": ["featured", "categories", "promotion", "bestsellers", "benefits", "newsletter"],
  "bold-contemporary": ["promotion", "categories", "bestsellers", "featured", "benefits", "newsletter"],
  "fashion-lifestyle": ["categories", "promotion", "featured", "bestsellers", "benefits", "newsletter"],
  "everyday-essentials": ["categories", "bestsellers", "promotion", "featured", "benefits", "newsletter"],
};

const DEFAULT_SECTION_ORDER = [
  "categories",
  "featured",
  "bestsellers",
  "promotion",
  "benefits",
  "newsletter",
];

const getImage = (value, fallback = "") => {
  if (!value || typeof value !== "string") return fallback;
  if (/^(https?:\/\/|\/)/i.test(value)) return value;
  if (/^data:image\//i.test(value)) {
    return sanitizeImageUrl(value) || fallback;
  }
  return fallback;
};

const StorefrontLink = ({ to, children, ...props }) =>
  /^https:\/\//i.test(to || "") ? (
    <a href={to} target="_blank" rel="noreferrer" {...props}>
      {children}
    </a>
  ) : (
    <Link to={to || "/shop"} {...props}>
      {children}
    </Link>
  );

const getCategoryImage = (category, index) =>
  getImage(
    category.imageUrl || category.ImageUrl,
    [
      "https://images.unsplash.com/photo-1490481651871-ab68de25d43d?auto=format&fit=crop&w=700&q=80",
      "https://images.unsplash.com/photo-1490312278390-ab64016e0aa9?auto=format&fit=crop&w=700&q=80",
      "https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&w=700&q=80",
    ][index % 3],
  );

const StorefrontHomepageTemplates = ({
  template,
  tenant,
  config,
  categories = [],
  products = [],
  promotions = [],
  configuredBanners = null,
}) => {
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [addingId, setAddingId] = useState(null);
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [activeHeroSlide, setActiveHeroSlide] = useState(0);
  const copy = TEMPLATE_COPY[template];
  const content = config.storefrontContent;
  const homepage = content.homepage || {};
  const announcement = content.announcementBar || {};
  const homeLayout = content.homeLayout || {};
  const categoryLayout = content.categoryLayout || {};
  const productGrid = content.productGrid || {};
  const mobileLayout = content.mobileLayout || {};
  const configuredSlides = Array.isArray(configuredBanners)
    ? configuredBanners
    : content.heroSlides || [];
  const storefrontSlides = configuredSlides.map((slide) => ({
    ...slide,
    imageUrl: slide.imageUrl || slide.image || "",
    buttonLink: slide.buttonLink || slide.link || "",
  }));
  const heroSlides = storefrontSlides.filter(
    (slide) => (slide.placement || "hero") === "hero",
  );
  const activeHeroSlideIndex = heroSlides.length
    ? activeHeroSlide % heroSlides.length
    : 0;
  const heroSlide = heroSlides[activeHeroSlideIndex];
  const showThemeDefaultHero = !heroSlide;
  useEffect(() => {
    if (heroSlides.length < 2) return undefined;
    const timer = window.setInterval(() => {
      setActiveHeroSlide((current) => (current + 1) % heroSlides.length);
    }, 5000);
    return () => window.clearInterval(timer);
  }, [heroSlides.length]);
  const contentBlocks = (content.contentBlocks || []).filter(
    (block) => block.enabled !== false,
  );
  const promotion = promotions[0];
  const title =
    homepage.heroTitle ||
    (showThemeDefaultHero ? copy.title : heroSlide.subtitle || copy.title);
  const description =
    homepage.heroDescription ||
    (showThemeDefaultHero
      ? copy.description
      : heroSlide.description || copy.description);
  const heroImage = getImage(
    homepage.heroImage ||
      (showThemeDefaultHero
        ? TEMPLATE_HERO_IMAGES[template]
        : heroSlide.imageUrl),
    TEMPLATE_HERO_IMAGES[template],
  );
  const heroButton =
    homepage.heroButtonText ||
    (showThemeDefaultHero
      ? "Explore the collection"
      : heroSlide.buttonText || "Explore the collection");
  const heroButtonLink =
    homepage.heroButtonLink ||
    (showThemeDefaultHero ? "/shop" : heroSlide.buttonLink || "/shop");
  const offerTitle = homepage.promotionTitle || promotion?.Name || "A little something extra";
  const offerDescription =
    homepage.promotionDescription ||
    promotion?.Description ||
    "Discover current offers selected for you.";
  const visibleCategories = categories
    .filter((category) => {
      const categoryId = category.id || category.CategoryID;
      return homepage.categoryVisibility?.[categoryId] !== false;
    })
    .slice(0, template === "everyday-essentials" ? 8 : 6);
  const availableProducts = products.filter(
    (product) =>
      product.variations?.length
        ? product.variations.some(
            (variation) => Number(variation.StockQuantity) > 0,
          )
        : Number(product.StockQuantity ?? 1) > 0,
  );
  const visibleProductCount = [8, 10, 12, 16, 20].includes(
    Number(productGrid.initialCount),
  )
    ? Number(productGrid.initialCount)
    : 10;
  const bestSellers = availableProducts
    .filter((product) => product.isBestSeller)
    .slice(0, visibleProductCount);
  const featuredProducts = availableProducts.slice(0, visibleProductCount);
  const showCategories = homeLayout.showCategoryMenu !== false;
  const showPromotion = homeLayout.showPromotions !== false;
  const animation = ["none", "fade", "slide"].includes(content.animation)
    ? content.animation
    : "none";
  const sections = useMemo(() => {
    const chosenOrder = Array.isArray(homepage.sectionOrder)
      ? homepage.sectionOrder
      : [];
    const defaults = TEMPLATE_LAYOUTS[template];
    const hasDefaultOrder =
      chosenOrder.length === DEFAULT_SECTION_ORDER.length &&
      chosenOrder.every(
        (section, index) => section === DEFAULT_SECTION_ORDER[index],
      );
    const sectionOrder = hasDefaultOrder ? defaults : chosenOrder;
    return [...new Set([...sectionOrder, ...defaults])].filter((section) =>
      defaults.includes(section),
    );
  }, [homepage.sectionOrder, template]);

  const handleAddToCart = async (product) => {
    if (product.variations?.length) {
      navigate(`/product/${product.ProductID}`);
      return;
    }
    if (!product?.ProductID || Number(product.StockQuantity) === 0) return;
    setAddingId(product.ProductID);
    try {
      const unitPrice = Number(product.SellingPrice || 0);
      await addToCart(product.ProductID, 1, {
        name: product.Name,
        price: unitPrice,
        originalPrice: Number(product.OriginalPrice || unitPrice),
        imageUrl: product.ImageUrl,
        sku: product.SKU,
      });
      toast.success(`${product.Name} added to your cart`);
    } catch (error) {
      console.error("Could not add product to cart:", error);
      toast.error("Could not add this item. Please try again.");
    } finally {
      setAddingId(null);
    }
  };

  const handleNewsletterSubmit = (event) => {
    event.preventDefault();
    toast.error(
      "Newsletter sign-up is not available yet. Please contact the store.",
    );
  };

  const renderProduct = (product) => {
    const variants = Array.isArray(product.variations) ? product.variations : [];
    const stock = variants.length
      ? variants.reduce(
          (total, variation) =>
            total + Math.max(0, Number(variation.StockQuantity) || 0),
          0,
        )
      : Number(product.StockQuantity ?? 0);
    const hasSale =
      Number(product.OriginalPrice || 0) > Number(product.SellingPrice || 0);
    return (
      <article className="sh-template-product" key={product.ProductID}>
        <Link
          className="sh-template-product-image"
          to={`/product/${product.ProductID}`}
          aria-label={`View ${product.Name}`}
        >
          <img src={product.ImageUrl} alt={product.Name} loading="lazy" />
          {hasSale && <span className="sh-template-sale">Special price</span>}
          {stock > 0 && stock <= 5 && (
            <span className="sh-template-stock">Only {stock} left</span>
          )}
        </Link>
        <div className="sh-template-product-info">
          <Link to={`/product/${product.ProductID}`} className="sh-template-product-name">
            {product.Name}
          </Link>
          {variants.length > 0 && (
            <span className="sh-template-variants">
              {variants.length} options
            </span>
          )}
          <div className="sh-template-product-buy">
            <span className="sh-template-price">
              {formatPrice(product.SellingPrice || 0)}
              {hasSale && (
                <del>{formatPrice(product.OriginalPrice)}</del>
              )}
            </span>
            <button
              type="button"
              aria-label={
                variants.length
                  ? `Choose options for ${product.Name}`
                  : `Add ${product.Name} to cart`
              }
              disabled={addingId === product.ProductID || stock === 0}
              onClick={() => handleAddToCart(product)}
            >
              {variants.length ? (
                <FiArrowRight />
              ) : addingId === product.ProductID ? (
                <FiCheck />
              ) : (
                <FiPlus />
              )}
              <span>
                {variants.length
                  ? "Choose"
                  : addingId === product.ProductID
                    ? "Added"
                    : "Add"}
              </span>
            </button>
          </div>
        </div>
      </article>
    );
  };

  const renderCategories = () =>
    showCategories &&
    visibleCategories.length > 0 && (
      <section className="sh-template-section sh-template-categories">
        <div className="sh-template-section-heading">
          <div>
            <span className="sh-template-eyebrow">BROWSE THE STORE</span>
            <h2>Find your next favourite</h2>
          </div>
          <Link to="/shop">All categories <FiArrowRight /></Link>
        </div>
        <div className="sh-template-category-grid">
          {visibleCategories.map((category, index) => (
            <Link
              className="sh-template-category"
              key={category.id || category.CategoryID}
              to={`/shop?category=${encodeURIComponent(category.id || category.CategoryID)}`}
            >
              <img src={getCategoryImage(category, index)} alt="" loading="lazy" />
              <span>{category.name}</span>
              <FiArrowRight aria-hidden="true" />
            </Link>
          ))}
        </div>
      </section>
    );

  const renderProducts = (items, label, heading, key) =>
    items.length > 0 && (
      <section className={`sh-template-section sh-template-${key}`}>
        <div className="sh-template-section-heading">
          <div>
            <span className="sh-template-eyebrow">{label}</span>
            <h2>{heading}</h2>
          </div>
          <Link to="/shop">Shop all <FiArrowRight /></Link>
        </div>
        <div className="sh-template-product-grid">
          {items.map((product, index) => (
            <React.Fragment key={product.ProductID}>
              {renderProduct(product)}
              {key === "featured" &&
                storefrontSlides
                  .filter(
                    (slide) =>
                      slide.placement === "inline" &&
                      (slide.pagePath || "/") === "/" &&
                      Number(slide.afterProducts || 4) === index + 1,
                  )
                  .map((slide) => renderInlineSlide(slide))}
            </React.Fragment>
          ))}
          {items.length > 0 &&
            storefrontSlides
              .filter(
                (slide) =>
                  slide.placement === "inline" &&
                  (slide.pagePath || "/") === "/" &&
                  Number(slide.afterProducts || 4) > items.length,
              )
              .map((slide) => renderInlineSlide(slide))}
        </div>
      </section>
    );

  const renderInlineSlide = (slide) => (
    <section className="sh-template-inline-slide" key={slide.id}>
      {(slide.imageUrl || slide.image) && (
        <img src={getImage(slide.imageUrl || slide.image)} alt="" loading="lazy" />
      )}
      <div>
        {slide.title && <span className="sh-template-eyebrow">{slide.title}</span>}
        {slide.subtitle && <h2>{slide.subtitle}</h2>}
        {slide.buttonText && (
          <StorefrontLink to={slide.buttonLink || "/shop"}>
            {slide.buttonText} <FiArrowRight />
          </StorefrontLink>
        )}
      </div>
    </section>
  );

  const renderContentBlock = (block) => (
    <StorefrontContentBlock
      key={block.id}
      block={block}
      promotions={promotions}
      animation={animation}
    />
  );

  const sectionContent = {
    categories: renderCategories(),
    featured:
      homepage.showFeaturedProducts === false
        ? null
        : renderProducts(featuredProducts, "SELECTED FOR YOU", "Featured finds", "featured"),
    bestsellers:
      homepage.showBestSellers === false || bestSellers.length === 0
        ? null
        : renderProducts(
            bestSellers,
            "CUSTOMER FAVOURITES",
            "Best sellers",
            "bestsellers",
          ),
    promotion: showPromotion && (
      <section className="sh-template-offer" key="offer">
        <div className="sh-template-offer-copy">
          <span className="sh-template-eyebrow">A STORE SPECIAL</span>
          <h2>{offerTitle}</h2>
          <p>{offerDescription}</p>
          <StorefrontLink to={homepage.promotionButtonLink || "/shop"}>
            {homepage.promotionButtonText || "Shop the offer"} <FiArrowRight />
          </StorefrontLink>
        </div>
        {(homepage.promotionImage || promotion?.ImageUrl) && (
          <img
            src={getImage(homepage.promotionImage || promotion?.ImageUrl)}
            alt=""
            loading="lazy"
          />
        )}
      </section>
    ),
    benefits:
      homepage.showBenefits === false
        ? null
        : (
            <section className="sh-template-benefits" aria-label="Shopping benefits">
              <div><FiTruck /><span><strong>Reliable delivery</strong><small>Across South Africa</small></span></div>
              <div><FiLock /><span><strong>Secure payments</strong><small>Shop with confidence</small></span></div>
              <div><FiHeadphones /><span><strong>Here to help</strong><small>Friendly customer support</small></span></div>
            </section>
          ),
    newsletter:
      homepage.showNewsletter === false
        ? null
        : (
            <section className="sh-template-newsletter">
              <div>
                <span className="sh-template-eyebrow">NOTES FROM OUR STORE</span>
                <h2>{config.storefrontContent.footer.newsletterTitle || "A good thing in your inbox"}</h2>
                <p>{config.storefrontContent.footer.newsletterText}</p>
              </div>
              <form onSubmit={handleNewsletterSubmit}>
                <label className="sh-template-visually-hidden" htmlFor="homepage-newsletter-email">Email address</label>
                <input
                  id="homepage-newsletter-email"
                  type="email"
                  required
                  placeholder="Your email address"
                  value={newsletterEmail}
                  onChange={(event) => setNewsletterEmail(event.target.value)}
                />
                <button type="submit"><FiMail /> Subscribe</button>
              </form>
            </section>
          ),
  };

  const homepageSections = [];
  const beforeProducts = contentBlocks.filter(
    (block) => block.position === "betweenHeroAndProducts",
  );
  const afterProducts = contentBlocks.filter(
    (block) => block.position === "betweenProductsAndReviews",
  );
  const productSectionIds = new Set(["featured", "bestsellers"]);
  const firstProductIndex = sections.findIndex((section) =>
    productSectionIds.has(section),
  );
  const lastProductIndex = sections
    .map((section) => productSectionIds.has(section))
    .lastIndexOf(true);
  let insertedBeforeProducts = false;

  sections.forEach((section, index) => {
    if (!insertedBeforeProducts && index === firstProductIndex) {
      homepageSections.push(...beforeProducts.map(renderContentBlock));
      insertedBeforeProducts = true;
    }
    homepageSections.push(
      <React.Fragment key={`section-${section}`}>
        {sectionContent[section]}
      </React.Fragment>,
    );
    if (index === lastProductIndex) {
      homepageSections.push(...afterProducts.map(renderContentBlock));
    }
  });
  if (!insertedBeforeProducts) {
    homepageSections.unshift(...beforeProducts.map(renderContentBlock));
  }
  if (lastProductIndex < 0) {
    homepageSections.push(...afterProducts.map(renderContentBlock));
  }

  const categoryColumns = [4, 6, 9].includes(
    Number(categoryLayout.desktopColumns),
  )
    ? Number(categoryLayout.desktopColumns)
    : 6;
  const desktopProductColumns = [3, 4, 5, 6].includes(
    Number(productGrid.desktopColumns),
  )
    ? Number(productGrid.desktopColumns)
    : 4;
  const mobileProductColumns = Number(productGrid.mobileColumns) === 1 ? 1 : 2;
  return (
    <div
      className={`sh-template-home sh-template-${template} storefront-animation-${animation}`}
      data-home-preset={homeLayout.preset || "balanced"}
      data-home-hero-size={homeLayout.heroSize || "standard"}
      data-home-category-size={categoryLayout.size || "standard"}
      data-home-category-menu-size={homeLayout.categoryMenuSize || "standard"}
      data-home-promotion-size={homeLayout.promotionSize || "standard"}
      data-mobile-hero-height={mobileLayout.heroHeight || "standard"}
      data-mobile-hero-alignment={mobileLayout.heroAlignment || "left"}
      data-mobile-category-tab-side={mobileLayout.categoryTabSide || "off"}
      data-mobile-card-style={mobileLayout.cardStyle || "standard"}
      data-mobile-product-spacing={mobileLayout.productSpacing || "comfortable"}
      style={{
        "--sh-category-columns": categoryColumns,
        "--sh-product-columns": desktopProductColumns,
        "--sh-mobile-product-columns": mobileProductColumns,
      }}
    >
      {announcement.enabled !== false && announcement.text && (
        <div className="sh-template-announcement">
          <span>{announcement.text}</span>
          {announcement.linkText && (
            <StorefrontLink to={announcement.link || "/shop"}>
              {announcement.linkText} <FiArrowRight />
            </StorefrontLink>
          )}
        </div>
      )}
      <form
        className="sh-template-search"
        role="search"
        onSubmit={(event) => {
          event.preventDefault();
          const query = searchQuery.trim();
          navigate(
            query ? `/shop?search=${encodeURIComponent(query)}` : "/shop",
          );
        }}
      >
        <FiSearch aria-hidden="true" />
        <label className="sh-template-visually-hidden" htmlFor="storefront-home-search">
          Search products
        </label>
        <input
          id="storefront-home-search"
          type="search"
          placeholder="Search products..."
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
        />
        <button type="submit">Search</button>
      </form>
      <section className="sh-template-hero">
        {homeLayout.showStoreTile !== false && tenant?.BusinessName && (
          <div className="sh-template-store-tile">
            <strong>{tenant.BusinessName}</strong>
            <span>Independent store</span>
          </div>
        )}
        <div className="sh-template-hero-copy">
          <span className="sh-template-eyebrow">{heroSlide?.title || copy.eyebrow}</span>
          <h1>{title}</h1>
          <p>{description}</p>
          <StorefrontLink
            className="sh-template-hero-button"
            to={heroButtonLink}
          >
            {heroButton} <FiArrowRight />
          </StorefrontLink>
          {template === "everyday-essentials" && (
            <p className="sh-template-hero-note"><FiCheck /> Easy shopping, honest prices.</p>
          )}
        </div>
        <div className="sh-template-hero-image">
          <img src={heroImage} alt={tenant?.BusinessName ? `${tenant.BusinessName} collection` : "Featured collection"} />
        </div>
        {template === "fashion-lifestyle" && (
          <span className="sh-template-lookbook-stamp">THE<br />NEW<br />EDIT</span>
        )}
      </section>
      {heroSlides.length > 1 && (
        <div className="sh-template-hero-pagination" aria-label="Hero slides">
          <button
            type="button"
            aria-label="Previous hero slide"
            onClick={() =>
              setActiveHeroSlide(
                (current) => (current - 1 + heroSlides.length) % heroSlides.length,
              )
            }
          >
            <FiChevronLeft />
          </button>
          <span>
            {activeHeroSlideIndex + 1} / {heroSlides.length}
          </span>
          <button
            type="button"
            aria-label="Next hero slide"
            onClick={() =>
              setActiveHeroSlide((current) => (current + 1) % heroSlides.length)
            }
          >
            <FiChevronRight />
          </button>
        </div>
      )}
      {mobileLayout.categoryTabSide !== "off" && showCategories && (
        <Link
          className={`sh-template-mobile-category-tab sh-template-mobile-category-tab--${mobileLayout.categoryTabSide || "left"}`}
          to="/shop"
          aria-label="Browse store categories"
        >
          <FiArrowRight />
          <span>Categories</span>
        </Link>
      )}
      <main className="sh-template-main">
        {homepageSections}
      </main>
    </div>
  );
};

export default StorefrontHomepageTemplates;
